"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../services/api";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import ChannelCard from "./ChannelCard";
import ChannelInvitationsCard from "./ChannelInvitationsCard";
import CreateChannelModal from "./modals/CreateChannelModal";
import JoinRequestModal from "./modals/JoinRequestModal";
import { showErrorToast } from "../../utils/notificationUtils";
import { resolveImageUrl, withPlaceholder } from "../../utils/imageUtils";
import "./ChannelList.css";

const ChannelList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { getUserType, user, isLoading: authLoading } = useAuth();
  
  // Track last request dependencies to prevent identical concurrent calls
  const lastRequestDepsRef = useRef(null);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const [channels, setChannels] = useState([]);
  const [myChannels, setMyChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [selectedChannelForRequest, setSelectedChannelForRequest] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all, my, requested
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const CARDS_PER_PAGE = 15; // 5 columns × 3 rows

  // Prevent duplicate API calls - use timestamp-based debouncing
  const lastRequestTimeRef = useRef(0);
  const isRequestInFlightRef = useRef(false);
  const abortControllerRef = useRef(null);
  const REQUEST_DEBOUNCE_MS = 100; // Minimum time between requests

  useEffect(() => {
    if (location.state?.channelLeftId) {
      const { channelLeftId, channelLeftAt, ...restState } = location.state;
      // Trigger a refresh when coming from a "leave channel" action
      setRefreshTrigger(prev => prev + 1);
      
      navigate(location.pathname + location.search, {
        replace: true,
        state: Object.keys(restState).length > 0 ? restState : null
      });
    }
  }, [location, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const now = Date.now();
    const isInitialLoad = !hasLoadedOnce;

    // WAIT for authentication to stabilize before making the initial fetch
    // This prevents redundant requests when 'user' object is populated after verify-token
    if (authLoading && isInitialLoad) {
      return;
    }

    // Check if dependencies are the same as last started request
    const currentDeps = JSON.stringify([debouncedSearch, user?.id, refreshTrigger]);
    
    // If request is in flight and deps haven't changed, skip
    if (isRequestInFlightRef.current && lastRequestDepsRef.current === currentDeps) {
      return;
    }

    // Apply debounce check - only for identical requests or extremely rapid changes
    // But if refreshTrigger changed, we should probably allow it
    const lastTrigger = lastRequestDepsRef.current ? JSON.parse(lastRequestDepsRef.current)[2] : 0;
    const triggerChanged = lastTrigger !== refreshTrigger;

    if (!triggerChanged && now - lastRequestTimeRef.current < REQUEST_DEBOUNCE_MS) {
      return;
    }

    // Set lock and timestamp IMMEDIATELY (synchronously) before any async operations
    isRequestInFlightRef.current = true;
    lastRequestTimeRef.current = now;
    lastRequestDepsRef.current = currentDeps;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (isInitialLoad) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    let cancelled = false;
    const currentUserId = user?.id || user?.user_id;
    const transformChannel = (channel, { treatAsDiscover = false } = {}) => {
      const channelId = Number(channel.id);
      const ownerId = channel.owner?.id ?? channel.owner?.user_id ?? channel.owner_id;
      const owner = (() => {
        const ownerObj = channel.owner || {};
        const ownerName =
          ownerObj.name ||
          [ownerObj.first_name, ownerObj.last_name].filter(Boolean).join(" ");
        return {
          id: ownerId,
          name: ownerName && ownerName.trim() ? ownerName : t("channelList.owner.unknown")
        };
      })();

      const rawRole =
        channel.user_role ||
        channel.userRole ||
        channel.role ||
        null;
      const isOwner = currentUserId && ownerId && Number(ownerId) === Number(currentUserId);
      const apiIsJoined = treatAsDiscover ? false : channel.is_joined ?? channel.isJoined;
      const baseIsJoined = apiIsJoined !== undefined ? apiIsJoined : isOwner;
      const finalIsJoined = isOwner ? true : Boolean(baseIsJoined);
      const memberCountValue = channel.members_count ?? channel.memberCount ?? 0;
      const adjustedMemberCount =
        isOwner && (!memberCountValue || memberCountValue < 1)
          ? 1
          : memberCountValue;

      const finalRole = rawRole ||
        (isOwner ? "owner" : baseIsJoined ? "member" : null);

      const profileImageUrl = resolveImageUrl(
        channel.profile_image_url,
        channel.profile_image,
        channel.profileImageUrl,
        channel.profileImage,
        channel.profile_image_path,
        channel.images?.profile,
        channel.media?.profile
      );

      return {
        id: channel.id,
        name: channel.name,
        description: channel.description,
        profileImage: withPlaceholder(profileImageUrl, "/images/social/pic-placeholder.png"),
        memberCount: adjustedMemberCount,
        postCount: channel.posts_count || channel.postCount || 0,
        isPublic: (channel.privacy_setting || channel.privacySetting) === 'public',
        isJoined: finalIsJoined,
        owner,
        userRole: (finalRole || "viewer").toLowerCase(),
        role: finalRole,
        joinRequestStatus: channel.join_request_status || (finalIsJoined ? 'joined' : 'none')
      };
    };

    const loadChannels = async () => {
      try {
        if (abortController.signal.aborted || cancelled) {
          return;
        }

        // Consolidated call: use 'all' filter which backend index supports
        // This returns public channels, joined channels, and owner channels in one go
        const response = await channelsAPI.getChannels({
          page: 1,
          per_page: 50,
          search: debouncedSearch || undefined,
          filter: 'all'
        });

        if (cancelled || abortController.signal.aborted) {
          return;
        }

        if (response.data && response.data.data) {
          const transformedChannels = response.data.data.map(channel => transformChannel(channel));
          
          // Separate out myChannels for local state if needed (though combined list is primary)
          const myChs = transformedChannels.filter(c => c.isJoined || c.userRole === 'owner');
          
          setMyChannels(myChs);
          setChannels(transformedChannels);
        } else {
          setChannels([]);
          setMyChannels([]);
        }
      } catch (error) {
        // Don't show error if request was aborted
        if (!cancelled && !abortController.signal.aborted) {
          handleApiError(error);
        }
      } finally {
        // Always clear the lock, even on error or abort
        isRequestInFlightRef.current = false;
        abortControllerRef.current = null;

        // Always clear loading states to prevent stuck UI
        setIsLoading(false);
        setIsRefreshing(false);

        // Only mark as loaded if request completed successfully
        if (!cancelled && !abortController.signal.aborted) {
          setHasLoadedOnce(true);
        }
      }
    };

    loadChannels();

    return () => {
      cancelled = true;
      // Abort any in-flight request on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [debouncedSearch, user?.id, user?.user_id, t, refreshTrigger, authLoading]);

  // Listen for channel update events to refresh channels reactively
  useEffect(() => {
    const handleChannelUpdate = (event) => {
      // Trigger a refresh by incrementing the refresh trigger
      // Use a small delay to ensure the backend has processed the change
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 500);
    };

    const handleRefresh = (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (eventChannelId) {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    const handleJoinRequestSync = (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (eventChannelId) {
        setChannels(prev => prev.map(c => 
          c.id === eventChannelId ? { ...c, joinRequestStatus: 'pending' } : c
        ));
      }
    };

    // Listen for custom events
    window.addEventListener('channel:joined', handleRefresh);
    window.addEventListener('channel:left', handleRefresh);
    window.addEventListener('channel:invitation-accepted', handleRefresh);
    window.addEventListener('channel:join-request-submitted', handleJoinRequestSync);
    window.addEventListener('channel:join-request-accepted', handleChannelUpdate);
    window.addEventListener('channel:created', handleChannelUpdate);

    return () => {
      window.removeEventListener('channel:joined', handleRefresh);
      window.removeEventListener('channel:left', handleRefresh);
      window.removeEventListener('channel:invitation-accepted', handleRefresh);
      window.removeEventListener('channel:join-request-submitted', handleJoinRequestSync);
      window.removeEventListener('channel:join-request-accepted', handleChannelUpdate);
      window.removeEventListener('channel:created', handleChannelUpdate);
    };
  }, []);

  const lowerSearchQuery = searchQuery.trim().toLowerCase();
  const filteredChannels = channels.filter(channel => {
    const channelName = (channel.name || "").toLowerCase();
    const channelDescription = (channel.description || "").toLowerCase();
    const matchesSearch =
      !lowerSearchQuery ||
      channelName.includes(lowerSearchQuery) ||
      channelDescription.includes(lowerSearchQuery);

    if (filter === "my") {
      // Show created communities (owner) OR joined communities
      const currentUserId = user?.id || user?.user_id;
      const ownerId = channel.owner?.id;
      const isOwner = currentUserId && ownerId && Number(ownerId) === Number(currentUserId);
      const isJoined = channel.isJoined;
      return matchesSearch && (isOwner || isJoined);
    } else if (filter === "requested") {
      // Show communities where join request is pending
      return matchesSearch && channel.joinRequestStatus === 'pending';
    }
    // "all" - show all communities
    return matchesSearch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredChannels.length / CARDS_PER_PAGE);
  const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
  const endIndex = startIndex + CARDS_PER_PAGE;
  const paginatedChannels = filteredChannels.slice(startIndex, endIndex);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, debouncedSearch]);

  const handleCreateChannel = () => {
    setShowCreateModal(true);
  };

  const handleChannelCreated = async (newChannel) => {
    try {
      setChannels(prev => [newChannel, ...prev]);
      setMyChannels(prev => [{ ...newChannel, role: "owner", isJoined: true }, ...prev]);
      setShowCreateModal(false);
      // Dispatch event to refresh channels in sidebar and other components
      window.dispatchEvent(new CustomEvent('channel:created', {
        detail: { channelId: newChannel.id }
      }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleJoinChannel = async (channelId) => {
    try {
      await channelsAPI.joinChannel(channelId);
      setChannels(prev => prev.map(c =>
        c.id === channelId ? { ...c, isJoined: true, memberCount: c.memberCount + 1 } : c
      ));
      const channel = channels.find(c => c.id === channelId);
      if (channel) {
        setMyChannels(prev => [...prev, { ...channel, role: "member", isJoined: true, memberCount: channel.memberCount + 1 }]);
      }
      // Dispatch event to refresh channels in sidebar
      window.dispatchEvent(new CustomEvent('channel:joined', {
        detail: { channelId }
      }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRequestJoin = async (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    if (channel) {
      setSelectedChannelForRequest(channel);
      setShowJoinRequestModal(true);
    }
  };

  const handleJoinRequestSent = async (message = '') => {
    if (selectedChannelForRequest) {
      const channelId = selectedChannelForRequest.id;
      
      // Optimistic update
      setChannels(prev => prev.map(c => 
        c.id === channelId ? { ...c, joinRequestStatus: 'pending' } : c
      ));

      // Close modal immediately
      setShowJoinRequestModal(false);
      
      // Dispatch global event
      window.dispatchEvent(new CustomEvent('channel:join-request-submitted', {
        detail: { 
          channelId,
          message 
        }
      }));

      setSelectedChannelForRequest(null);
    }
  };


  const handleCancelJoinRequest = async (channelId) => {
    try {
      await channelsAPI.cancelJoinRequest(channelId);
      // Update channel status
      setChannels(prev => prev.map(c =>
        c.id === channelId ? { ...c, joinRequestStatus: 'none' } : c
      ));
      // Refresh channels to get updated status
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleLeaveChannel = async (channelId) => {
    try {
      await channelsAPI.leaveChannel(channelId);
      setChannels(prev => prev.map(c =>
        c.id === channelId ? { ...c, isJoined: false, joinRequestStatus: 'none', memberCount: Math.max(0, c.memberCount - 1) } : c
      ));
      setMyChannels(prev => prev.filter(c => c.id !== channelId));
      window.dispatchEvent(new CustomEvent('channel:left', {
        detail: { channelId }
      }));
    } catch (error) {
      const errorMessage = handleApiError(error);
      showErrorToast(errorMessage);
    }
  };

  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="channel-list-container">
        <div className="channel-list-loading">
          <div className="loading-spinner"></div>
          <p>{t("channelList.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="channel-list-container" style={{ '--user-type-color': userTypeColor }}>
      {isRefreshing && hasLoadedOnce && (
        <div className="channel-list-refresh-indicator">
          <div className="loading-spinner small"></div>
          <span>{t("channelList.refreshing", { defaultValue: "Updating channels…" })}</span>
        </div>
      )}

      <div className="channel-list-header">
        <div className="channel-list-header-content">
          <h1 className="channel-list-title">{t("channelList.title")}</h1>
          <p className="channel-list-subtitle">{t("channelList.subtitle")}</p>
        </div>
        <button className="create-channel-btn" onClick={handleCreateChannel}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          {t("channelList.createButton")}
        </button>
      </div>

      <div className="channel-list-filters">
        <div className="channel-search">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder={t("channelList.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="channel-search-input"
          />
        </div>
        <div className="channel-filter-tabs">
          <button
            className={`filter-tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            {t("channelList.filters.all")}
          </button>
          <button
            className={`filter-tab ${filter === "my" ? "active" : ""}`}
            onClick={() => setFilter("my")}
          >
            {t("channelList.filters.my")}
          </button>
          <button
            className={`filter-tab ${filter === "requested" ? "active" : ""}`}
            onClick={() => setFilter("requested")}
          >
            {t("channelList.filters.requested")}
          </button>
        </div>
      </div>

      {/* Invitations card sits above the main grid, full width */}
      <ChannelInvitationsCard
        onAccepted={() => {
          // trigger a refresh of channels/myChannels
          setHasLoadedOnce(false);
        }}
      />

      <div className="channel-list-grid">
        {paginatedChannels.length > 0 ? (
          paginatedChannels.map(channel => {
            // Determine action handlers based on join_request_status
            let onJoin, onCancel, onLeave;
            if (channel.joinRequestStatus === 'joined') {
              onLeave = () => handleLeaveChannel(channel.id);
            } else if (channel.joinRequestStatus === 'pending') {
              onCancel = () => handleCancelJoinRequest(channel.id);
            } else {
              // none - show join button
              onJoin = channel.isPublic
                ? () => handleJoinChannel(channel.id)
                : () => handleRequestJoin(channel.id);
            }

            return (
              <ChannelCard
                key={channel.id}
                channel={channel}
                onJoin={onJoin}
                onCancel={onCancel}
                onLeave={onLeave}
                onView={() => navigate(`/social/channels/${channel.id}`)}
              />
            );
          })
        ) : (
          <div className="channel-list-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
            <p>
              {filter === "requested"
                ? t("channelList.empty.requested", { defaultValue: "No pending join requests" })
                : filter === "my"
                  ? t("channelList.empty.my", { defaultValue: "You haven't joined or created any communities yet" })
                  : t("channelList.empty.title", { defaultValue: "No communities found" })
              }
            </p>
            {filter === "all" && (
              <button className="create-channel-btn-secondary" onClick={handleCreateChannel}>
                {t("channelList.empty.action", { defaultValue: "Create Your First Community" })}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="channel-list-pagination">
          <button
            className="channel-list-page-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* Show page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                className={`channel-list-page-num ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}

          {totalPages > 5 && currentPage < totalPages - 2 && (
            <span className="channel-list-page-dots">...</span>
          )}

          {totalPages > 5 && currentPage < totalPages - 2 && (
            <button
              className="channel-list-page-num"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          )}

          <button
            className="channel-list-page-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      )}

      {showCreateModal && (
        <CreateChannelModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onChannelCreated={handleChannelCreated}
        />
      )}

      {showJoinRequestModal && selectedChannelForRequest && (
        <JoinRequestModal
          isOpen={showJoinRequestModal}
          onClose={() => {
            setShowJoinRequestModal(false);
            setSelectedChannelForRequest(null);
          }}
          channelId={selectedChannelForRequest.id}
          channelName={selectedChannelForRequest.name}
          onRequestSent={handleJoinRequestSent}
        />
      )}
    </div>
  );
};

export default ChannelList;

